import { useEffect, useRef, useState } from 'react';

import { Alert } from '@design-system/components/alert/alert';
import { Modal } from '@design-system/components/modal/modal';
import { Typography } from '@design-system/components/typography/typography';
import { Tag } from '@design-system/components/tag/tag';
import { CopyCard } from '@design-system/components/modals/CopyLinkModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import {
  CollectionLink,
  CollectionOffer,
  OtpLinksModalAlert,
  ProgramLinksModalData,
} from '../../../collections/collections.domain';
import * as styles from './programLinksModal.css';

const OTP_ALERT_CONTENT: Record<
  OtpLinksModalAlert,
  { title: string; details: string }
> = {
  'otp-migrated': {
    title: 'Your OTP Programs are part of Offers now.',
    details:
      "If your program is part of more than 1 Offer, you can select which Offer you'd like the links for below",
  },
  'multiple-offers': {
    title: 'This Program is included in multiple offers.',
    details:
      "Use the drop-down below to select the offer you're interested in and you'll be able to grab the appropriate links right here.",
  },
};

const BundleLabel = ({ bundle }: { bundle: CollectionOffer }) => (
  <span className={styles.bundleLabel}>
    <Typography
      as="span"
      variant="body"
      size="medium"
      fontWeight="medium"
      color="N-100"
    >
      {bundle.name}
    </Typography>
    <Typography
      as="span"
      variant="body"
      size="small"
      fontWeight="normal"
      color="N-80"
      className={styles.contentDescription}
    >
      {bundle.contentDescription}
    </Typography>
    <Typography
      as="span"
      variant="body"
      size="medium"
      fontWeight="medium"
      color="N-100"
    >
      {bundle.price}
    </Typography>
  </span>
);

const BundleDropdown = ({
  bundles,
  selectedIndex,
  onSelect,
}: {
  bundles: Array<CollectionOffer>;
  selectedIndex: number;
  onSelect: (index: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedBundle = bundles[selectedIndex] ?? bundles[0];

  return (
    <div className={styles.dropdownWrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.dropdownTrigger}
        onClick={() => setOpen((prev) => !prev)}
      >
        <BundleLabel bundle={selectedBundle} />
        <FontAwesomeIcon icon={faCaretDown} />
      </button>
      {open ? (
        <div className={styles.dropdownMenu}>
          {bundles.map((bundle, index) => (
            <div
              key={`${bundle.name}-${index}`}
              className={styles.dropdownOption}
              onClick={() => {
                onSelect(index);
                setOpen(false);
              }}
            >
              <BundleLabel bundle={bundle} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const LinksList = ({ links }: { links: Array<CollectionLink> }) => (
  <div className="flex flex-col gap-4">
    {links.map((link) => (
      <CopyCard
        url={link.url}
        linkTitle={link.title}
        onEdit={link.onEdit}
        key={link.url}
      >
        <Typography variant="body" size="medium" fontWeight="normal">
          {link.description}
        </Typography>
      </CopyCard>
    ))}
  </div>
);

const SubscriptionContent = ({
  programName,
  links,
}: {
  programName: string;
  links: Array<CollectionLink>;
}) => (
  <>
    <Tag
      label="Sold as subscription"
      readonly
      className={styles.subscriptionTag}
      labelClassName={styles.subscriptionTagLabel}
    />
    <Typography>
      Program:{' '}
      <Typography as="span" fontWeight="bold">
        {programName}
      </Typography>{' '}
      direct links
    </Typography>
    <LinksList links={links} />
  </>
);

const DirectContent = ({
  programName,
  links,
  afterDesc,
}: {
  programName: string;
  links: Array<CollectionLink>;
  afterDesc?: string;
}) => (
  <>
    <Typography>
      Program:{' '}
      <Typography as="span" fontWeight="bold">
        {programName}
      </Typography>{' '}
      links
    </Typography>
    <LinksList links={links} />
    {afterDesc ? (
      <Typography variant="body" size="medium" fontWeight="normal" color="N-80">
        {afterDesc}
      </Typography>
    ) : null}
  </>
);

const OtpContent = ({
  programName,
  bundles,
  alert,
}: {
  programName: string;
  bundles: Array<CollectionOffer>;
  alert?: OtpLinksModalAlert;
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedBundle = bundles[selectedIndex] ?? bundles[0];

  if (!selectedBundle) return null;

  const alertContent = alert ? OTP_ALERT_CONTENT[alert] : null;

  return (
    <>
      {alertContent ? (
        <Alert
          variant="info"
          title={alertContent.title}
          details={alertContent.details}
          alignStart
        />
      ) : null}
      <Typography>
        Program:{' '}
        <Typography as="span" fontWeight="bold">
          {programName}
        </Typography>{' '}
        is part of offer(s):
      </Typography>
      <BundleDropdown
        bundles={bundles}
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
      />
      <LinksList links={selectedBundle.links} />
    </>
  );
};

export const ProgramLinksModal = ({
  isOpen,
  onClose,
  data,
}: {
  isOpen: boolean;
  onClose: () => void;
  data?: ProgramLinksModalData;
}) => {
  if (!isOpen || !data) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Sharing links?"
      disableBackdropClick={true}
      modalClassName="w-full md:w-[742px]"
    >
      <div className="flex flex-col gap-6">{renderContent(data)}</div>
    </Modal>
  );
};

const renderContent = (data: ProgramLinksModalData) => {
  switch (data.type) {
    case 'subscription':
      return (
        <SubscriptionContent
          programName={data.programName}
          links={data.links}
        />
      );
    case 'otp':
      return (
        <OtpContent
          programName={data.programName}
          bundles={data.bundles}
          alert={data.alert}
        />
      );
    case 'direct':
      return (
        <DirectContent
          programName={data.programName}
          links={data.links}
          afterDesc={data.afterDesc}
        />
      );
  }
};
